import { Box, Button, Divider, Popover, Tab, Tabs, Typography } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core";
import { Close, ExpandMore } from "@material-ui/icons";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { blueContrast, useMapThemeClasses } from "../../hooks/use-map-theme-styles";
import { onlyFirstLetterUppercase } from "../../utils/first-letter-uppercase";
import { FilterStateType, ListFilterState, TableFilterAction, isFilterEmpty } from "./models";
import { renderFilterComponent } from "./renderFilterComponent";

function TabPanel(props: { children: React.ReactNode; value: number; index: number; style?: React.CSSProperties }) {
	const { children, value, index } = props;
	const isActive = value === index;

	return (
		<Box
			role="tabpanel"
			hidden={!isActive}
			id={`tabs-group-tabpanel-${index}`}
			aria-labelledby={`tabs-group-tab-${index}`}
			style={{
				display: isActive ? "block" : "none",
				width: "100%",
			}}
		>
			{isActive && <Box>{children}</Box>}
		</Box>
	);
}

function a11yProps(index: number) {
	return {
		id: `tabs-group-tab-${index}`,
		"aria-controls": `tabs-group-tabpanel-${index}`,
	};
}

const PermanentFilterButton = withStyles((theme) => ({
	root: {
		borderRadius: "4px",
		borderWidth: "1px",
		borderStyle: "solid",
		borderColor: blueContrast,
		padding: 0,
		paddingLeft: theme.spacing(1),
		height: "auto",
		textTransform: "none",
		width: "auto",
	},
}))(Button);

export const ClearPermanentFilterButton = withStyles({
	root: {
		padding: "0 2px 0 2px",
		height: "auto",
		minWidth: 0,
	},
})(Button);

interface TabsGroupTab {
	filter: FilterStateType;
	labelKey?: string;
}

interface TabsGroupProps {
	tabs: TabsGroupTab[];
	usePopover: boolean;
	dispatcher: (action: TableFilterAction) => void;
	customOnSelectAction?: () => void;
	handlePopoverClose?: () => void;
	exclusive?: boolean;
	onActiveTabChange?: (activeTabIndex: number) => void;
}

const useStyles = makeStyles({
	tabsRoot: {
		backgroundColor: "#F5F8F9",
		height: "40px",
		padding: " 6px",
		borderRadius: "4px",
		margin: "16px 16px 0 16px",
		maxWidth: "fit-content",
	},
	tabRoot: {
		"& .MuiTab-wrapper": {
			flexShrink: 0,
			marginBottom: "11px",
			fontSize: "16px",
			fontWeight: 600,
		},
	},
	tabActiveTitle: {
		maxWidth: "fit-content",
		borderRadius: "4px",
		background: "#FFF",
		boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.08)",
		color: "#367BF5",
		height: "27px",
	},
	tabInactive: {
		color: "#969392",
	},
	tabPanelContainer: {
		position: "relative",
		width: "100%",
	},
	tabPanel: {
		padding: "6px",
		paddingTop: "4px",
		paddingBottom: "8px",
		"& [class*='ellipsisRule']": {
			overflow: "visible",
		},
	},
});

const TabsGroup: React.FC<TabsGroupProps> = ({
	tabs,
	usePopover,
	dispatcher,
	customOnSelectAction,
	handlePopoverClose,
	exclusive = true,
	onActiveTabChange,
}) => {
	const { t } = useTranslation();
	const { tabIndicator, tabTitle } = useMapThemeClasses();
	const classes = useStyles();
	const selectedTabIndex = useMemo(() => tabs.findIndex((tab) => !isFilterEmpty(tab.filter)), [tabs]);
	const [activeTab, setActiveTab] = useState(selectedTabIndex >= 0 ? selectedTabIndex : 0);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (selectedTabIndex < 0) {
			return;
		}
		setActiveTab((prevActiveTab) => (prevActiveTab === selectedTabIndex ? prevActiveTab : selectedTabIndex));
	}, [selectedTabIndex]);

	useEffect(() => {
		onActiveTabChange?.(activeTab);
	}, [activeTab]);

	const handleTabChange = (_event: React.ChangeEvent<Record<string, unknown>>, newTabValue: number) => {
		if (exclusive) {
			tabs.forEach((tab, index) => {
				if (index !== newTabValue) {
					dispatcher({ type: "clear-selection", path: tab.filter.field as string });
				}
			});
			customOnSelectAction?.();
		}
		setActiveTab(newTabValue);
	};

	const togglePopover = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handlePopoverCloseInternal = () => {
		setAnchorEl(null);
		handlePopoverClose?.();
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		tabs.forEach((tab) => {
			dispatcher({ type: "clear-selection", path: tab.filter.field as string });
		});
		customOnSelectAction?.();
	};

	const preventDefault = useCallback((e: any) => {
		e = e || window.event;
		if (e.preventDefault) {
			e.preventDefault();
		}
		e.returnValue = false;
	}, []);

	const enableScroll = () => {
		document.removeEventListener("wheel", preventDefault, false);
	};

	const disableScroll = () => {
		document.addEventListener("wheel", preventDefault, {
			passive: false,
		});
	};

	const isOpen = !!anchorEl;
	const isEmpty = tabs.every((tab) => isFilterEmpty(tab.filter));

	// Get active filter content for button display
	const getActiveFilterContent = () => {
		const tabWithSelection = tabs.find((tab) => !isFilterEmpty(tab.filter));

		if (tabWithSelection) {
			const filter = tabWithSelection.filter;
			if (filter.selectionType === "list") {
				const listFilter = filter as ListFilterState;
				const firstSelectedOption = listFilter.options.find((option) => option.isSelected);
				if (firstSelectedOption) {
					return (
						<Typography
							color="primary"
							style={{
								maxWidth: 300,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{firstSelectedOption.label}
						</Typography>
					);
				}
			}
		}

		const activeTabData = tabs[activeTab];
		const labelKey = activeTabData?.labelKey || activeTabData?.filter.localizationKey;

		return <Typography style={{ whiteSpace: "nowrap" }}>{onlyFirstLetterUppercase(t(labelKey || "filter"))}</Typography>;
	};

	const handleDispatchForTab = (filterField: string) => {
		return (action: TableFilterAction) => {
			if ("path" in action) {
				const nextAction = action.path ? action : { ...action, path: filterField };
				dispatcher(nextAction as TableFilterAction);
			} else {
				dispatcher(action);

				return;
			}

			if (!exclusive) {
				return;
			}

			if (action.type === "select-list-value" || action.type === "set-boolean-value" || action.type === "set-values") {
				tabs.forEach((tab) => {
					const tabField = tab.filter.field as string;
					if (tabField !== filterField) {
						dispatcher({ type: "clear-selection", path: tabField });
					}
				});
			}
		};
	};

	const tabsContent = (
		<Box minWidth="200px">
			<Tabs
				className={classes.tabsRoot}
				TabIndicatorProps={{
					className: tabIndicator,
				}}
				value={activeTab}
				onChange={handleTabChange}
				aria-label="filter tabs"
				classes={{ root: tabTitle }}
			>
				{tabs.map((tab, index) => {
					const isActive = activeTab === index;
					const labelKey = tab.labelKey || tab.filter.localizationKey || (tab.filter.field as string);

					return (
						<Tab
							key={`${String(tab.filter.field)}-${index}`}
							label={t(labelKey)}
							{...a11yProps(index)}
							classes={{
								root: isActive
									? `${classes.tabRoot} ${classes.tabActiveTitle}`
									: `${classes.tabRoot} ${classes.tabInactive}`,
							}}
							style={{ textTransform: "none" }}
						/>
					);
				})}
			</Tabs>
			<Box className={classes.tabPanelContainer}>
				{tabs.map((tab, index) => (
					<TabPanel key={`${String(tab.filter.field)}-${index}`} value={activeTab} index={index}>
						<Box className={classes.tabPanel}>
							{renderFilterComponent(tab.filter, {
								usePopover,
								dispatcher: handleDispatchForTab(tab.filter.field as string),
								customOnSelectAction: () => {
									customOnSelectAction?.();
									// Close popover if single select
									if (
										tab.filter.selectionType === "list" &&
										!(tab.filter as ListFilterState).isMulti &&
										handlePopoverCloseInternal
									) {
										handlePopoverCloseInternal();
									}
								},
								handlePopoverClose: handlePopoverCloseInternal,
								isTabsGroup: true,
							})}
						</Box>
					</TabPanel>
				))}
			</Box>
		</Box>
	);

	if (!usePopover) {
		return tabsContent;
	}

	return (
		<>
			<Box pr={1} onMouseEnter={disableScroll} onMouseLeave={enableScroll}>
				<PermanentFilterButton
					variant="outlined"
					aria-haspopup="true"
					onClick={togglePopover}
					style={{ paddingLeft: isEmpty ? undefined : 0 }}
				>
					<Box maxWidth="100%" display="flex" alignItems={"center"} height={"26px"}>
						{!isEmpty && (
							<>
								<ClearPermanentFilterButton onClick={handleClear} style={{ flexShrink: 0 }}>
									<Close color="primary" style={{ fontSize: "1.1rem" }} />
								</ClearPermanentFilterButton>
								<Divider orientation="vertical" style={{ marginRight: "8px", backgroundColor: blueContrast }} />
							</>
						)}
						{isEmpty ? (
							<Typography style={{ whiteSpace: "nowrap" }}>{getActiveFilterContent()}</Typography>
						) : (
							<Box display={"flex"} alignItems="center">
								{getActiveFilterContent()}
							</Box>
						)}
						<ExpandMore color="primary" />
					</Box>
				</PermanentFilterButton>
			</Box>
			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				onClose={handlePopoverCloseInternal}
				disableRestoreFocus
				disableScrollLock
			>
				{tabsContent}
			</Popover>
		</>
	);
};

export default memo(TabsGroup);
